"use client";

import { useMemo, useState, useEffect, useContext, useRef } from "react";
import { DataGrid, GridFooterContainer, GridFooter } from "@mui/x-data-grid";
import {
  IconButton,
  Typography,
  Box,
  Paper,
  Tooltip,
  Chip,
  alpha,
  Button,
  TextField,
  InputAdornment,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import { useNavigate } from "react-router-dom";


import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Inventory2Icon from "@mui/icons-material/Inventory2";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PaymentIcon from "@mui/icons-material/Payment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteIcon from "@mui/icons-material/Delete";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { ThemeProvider, createTheme } from "@mui/material/styles";
import dayjs from "dayjs";

import { ErpContext } from "../../context/ErpContext.js";
import "./TablaVentasComponent.css";
import SpinnerLading from "../misc/SpinnerComponent.js";
import ModalViewTablaCompra from "./ModalViewTablaCompra.js";
import CustomPagination from "./CustomPagination";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import ModalMedioDePago from "./ModalMedioDePago.js";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#5b5b5f" },
    secondary: { main: "rgb(244, 177, 14)" },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": { boxShadow: "0 4px 8px rgba(0,0,0,0.1)" },
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { borderRadius: 12 } } },
    MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
  },
});

export default function TablaCompras() {
  // Variables y funciones del context (sin cambios)
  const {
    getMoneda,
    getTipoTransaccion,
    getUbicacion,
    ubicaciones,
    getNegocio,
    getMedioDePago,
    getSubCategoriaTransaccion,
    getTransaccionItem,
    getTransaccionItemIDEnCompras,
    getTransaccionItemID,
    getItem,
    generarFacturaPDF,
    getItemUbicacion,
    getUsuario,
    getTransaccionTransformedCompras,
    getEntidadByTipoEntidad,
    getItemById,
    getItemUbicacionFilter,
    getEstadoCompra,
    getEntidadByID,
    updateDeleteTransaccion,
    isNavbarCollapsed,
    getCuentaCorrienteByIdEntidad,
    postCrearPago,
    updateCuentaCorriente,
    postCuentaCorriente,
    updateTransaccionCompraEstado,
    postTransaccionPago,
  } = useContext(ErpContext);

  const navigate = useNavigate();

    const estadoCompraGlobal = useRef(1); // Se actualiza de forma inmediata y no provoca re-render
  

  // Estados propios del componente
  const [openModal, setOpenModal] = useState(false);
  const [transaccionItems, setTransaccionItems] = useState([]);
  const [compraItems, setTransaccionCompraItems] = useState([]);
  const [selectedTransaccion, setSelectedTransaccion] = useState(null);
  const [selectedTransaccionEntidad, setSelectedTransaccionEntidad] =
    useState(null);
  const [loading, setLoading] = useState(true);
  const [sortedData, setSortedData] = useState([]);
  const [itemsMap, setItemsMap] = useState({});
  const [estadoSeleccionado, setEstadoSeleccionado] = useState(null);
  const [estados, setEstados] = useState([]);

  // Estado para proveedores (entidades) – queremos sólo las de ID 2
  const [entidades, setEntidades] = useState([]);

  // Estados para filtros
  const today = dayjs();
  const [startDate, setStartDate] = useState(today.subtract(30, "day"));
  const [endDate, setEndDate] = useState(today);
  const [minCompras, setMinCompras] = useState(0);
  const [maxCompras, setMaxCompras] = useState(0);
  const [proveedor, setProveedor] = useState(null);
  const [searchTextProveedor, setSearchTextProveedor] = useState("");
  const [selectedUbicacionFiltro, setSelectedUbicacionFiltro] = useState(null);
  const [page, setPage] = useState(null);
  const [limit, setLimit] = useState(100);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [mostrarDeletes, setMostrarDeletes] = useState(false);
  const [idTransaccionSearch, setIdTransaccionSearch] = useState(null);

  const [montoTotalTransacciones, setMontoTotalTransacciones] = useState(0)

  const isFirstRender = useRef(true);

  //modal medio de pagos
  const [abrirMedioDePago, setAbrirMedioDePago] = useState(false);
  const [precioTotal, setPrecioTotal] = useState(0);


  // Funciones para manipular fechas y montos
  const increaseDate = () =>
    setStartDate((prev) => (prev ? prev.add(1, "day") : dayjs()));
  const decreaseDate = () =>
    setStartDate((prev) => (prev ? prev.subtract(1, "day") : dayjs()));
  const handleMinChange = (e) => {
    const newMin = Number(e.target.value);
    if (newMin <= maxCompras) setMinCompras(newMin);
  };
  const handleMaxChange = (e) => {
    const newMax = Number(e.target.value);
    if (newMax >= minCompras) setMaxCompras(newMax);
  };

  // Función para obtener las compras filtradas, pasando el id del estado si se selecciona
  const fetchData = async (
    date,
    page,
    endDate,
    proveedor,
    selectedUbicacionFiltro,
    valorMin,
    valorMax,
    limit = 100,
    estadoSeleccionado,
    mostrarDeletes,
    idTransaccionSearch
  ) => {
    const controller = new AbortController();
    const signal = controller.signal;
    try {
      setLoading(true);
      const formattedDate = date.format("YYYY-MM-DD");
      const formattedDate2 =
        endDate && typeof endDate.format === "function"
          ? endDate.format("YYYY-MM-DD")
          : endDate;
      const idProveedor = proveedor ? proveedor.id : null;
      const idUbicacion = selectedUbicacionFiltro
        ? selectedUbicacionFiltro.id
        : null;

      const data = await getTransaccionTransformedCompras(
        2, // Compras
        formattedDate,
        formattedDate2,
        page,
        limit,
        { signal },
        idProveedor,
        idUbicacion,
        minCompras,
        maxCompras,
        estadoSeleccionado ? estadoSeleccionado.id : "",
        mostrarDeletes,
        idTransaccionSearch
      );

      console.log("Data de compras:", data);

      const transaccionesCompras = data.data || [];
      const transaccionesReforged = transaccionesCompras.map((transaccion) => {
        const pagos = transaccion.pagos.map((pago) => ({
          descripcion: pago.medioDePago,
          monto: pago.montoTotal,
        }));

        const formattedDate = new Intl.DateTimeFormat("es-AR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(transaccion.fechaHoraCreacion));

        return {
          id: transaccion.id,
          idEstadoDeCompra:
            transaccion.transaccionCompraEstado?.estadoCompra.id ||
            "No disponible",
          createdAt: formattedDate,
          montoTotal: transaccion.montoTotal,
          idMoneda: transaccion.pagos?.[0]?.cotizacion || "",
          idTipoTransaccion: transaccion.tipoTransaccion?.descripcion || "",
          pagos,
          descripcionEntidad: transaccion.entidad?.descripcion || "",
          textoDescripcion: transaccion.descripcion || "",
          dniCuitCuil: transaccion.entidad?.dniCuitCuil || "",
          apellido: transaccion.entidad?.apellido || "",
          direccion: transaccion.entidad?.direccion || "",
          localidad: transaccion.entidad?.localidad || "",
          provincia: transaccion.entidad?.provincia || "",
          usuario: transaccion.usuario?.usuario || "",
          negocio: transaccion.negocio?.descripcion || "",
          ubicacion: transaccion.ubicacion?.descripcion || "",
          idEntidadNumero: transaccion.idEntidad,
          fechaHoraCreacion: transaccion.fechaHoraCreacion,
          impuestos: transaccion.impuestos,
          estadoDeCompra:
            transaccion.transaccionCompraEstado?.estadoCompra.descripcion ||
            "No disponible",
          idEstadoDeCompra:
            transaccion.transaccionCompraEstado?.estadoCompra.id ||
            "No disponible",
        };
      });

      setPage(data.currentPage);
      setTotalPages(data.totalPages);
      setTotalCount(data.total);
      setMontoTotalTransacciones(data.totalMontoVentas)
      setSortedData(transaccionesReforged.sort((a, b) => b.id - a.id));
    } catch (error) {
      console.error("Error al cargar las compras:", error);
    } finally {
      setLoading(false);
    }
  };

  // Botón Buscar: resetea la página a 1 y envía el id del estado (si está seleccionado)
  const buttonFiltrado = () => {
    setPage(1);
    fetchData(
      startDate,
      1,
      endDate,
      proveedor,
      selectedUbicacionFiltro,
      minCompras,
      maxCompras,
      100,
      estadoSeleccionado ? estadoSeleccionado : null,
      mostrarDeletes,
      idTransaccionSearch
    );
  };

  // Resetear filtros
  const resetFilters = () => {
    setProveedor(null);
    setSearchTextProveedor("");
    setSelectedUbicacionFiltro(null);
    setMinCompras(0);
    setMaxCompras(0);
    setEstadoSeleccionado(null);
    const today = dayjs();
    setStartDate(today.subtract(30, "day"));
    setEndDate(today);
    setPage(1);
    setMostrarDeletes(null);
    setIdTransaccionSearch("");
    fetchData(
      today.subtract(30, "day"),
      1,
      today,
      null,
      null,
      0,
      0,
      100,
      null,
      null,
      null
    );
  };

  // Carga inicial (se ejecuta una sola vez)
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchInicio = async () => {
      try {
        setPage(1);
        await getMoneda();
        await getTipoTransaccion();
        await getUbicacion();
        await getNegocio();
        // Obtener proveedores: solo las entidades con ID 2
        const proveedores = await getEntidadByTipoEntidad(2);
        console.log("proveedores ", proveedores);
        const estados = await getEstadoCompra();
        // Aseguramos que sea un array
        setEntidades(Array.isArray(proveedores) ? proveedores : [proveedores]);
        setEstados(Array.isArray(estados) ? estados : [estados]);
        await getMedioDePago();
        await getSubCategoriaTransaccion();
        await getTransaccionItem();
        await getItem();
        await getItemUbicacion();
        await getUsuario();
        // Cargar compras con filtros por defecto
        //fetchData(startDate, 1, endDate, proveedor, selectedUbicacionFiltro, minCompras, maxCompras, 100, estadoSeleccionado ? estadoSeleccionado : null)
      } catch (error) {
        console.error("Error al cargar datos iniciales:", error);
      }
    };

    fetchInicio();

    return () => {
      controller.abort();
    };
  }, []);

  // Actualizar datos cuando cambia la página (para evitar ejecuciones duplicadas)
  useEffect(() => {
    if (!isFirstRender.current) {
      console.log("entro aca el renderrrrrrrr");
      fetchData(
        startDate,
        page,
        endDate,
        proveedor,
        selectedUbicacionFiltro,
        minCompras,
        maxCompras,
        100,
        estadoSeleccionado ? estadoSeleccionado : null,
        mostrarDeletes,
        idTransaccionSearch
      );
    } else {
      isFirstRender.current = false;
    }
  }, [page]);

  // Definición de columnas (sin cambios)
  const columns = [
    { field: "id", headerName: "ID", width: 70, headerAlign: "center", },
    {
      field: "createdAt",
      headerName: "Fecha",
      width: 200,
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CalendarTodayIcon
            fontSize="small"
            sx={{ color: alpha("#5b5b5f", 0.7) }}
          />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "montoTotal",
      headerName: "Monto",
      type: "number",
      width: 130,
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          icon={<AttachMoneyIcon fontSize="small" />}
          label={params.value?.toLocaleString("es-AR") || "0.00"}
          size="small"
          sx={{
            fontWeight: "bold",
            bgcolor: alpha("#4CAF50", 0.1),
            color: "#4CAF50",
            "& .MuiChip-icon": { color: "#4CAF50" },
            display: "flex",
          }}
        />
      ),
    },
    {
      field: "pagos",
      headerName: "Medio de Pago",
      width: 200,
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => {
        const pagos = Array.isArray(params.row.pagos) ? params.row.pagos : [];
        if (pagos.length > 1) {
          return (
            <Chip
              label="Múltiple"
              size="small"
              color="info"
              sx={{ fontWeight: "bold", display: "flex" }}
            />
          );
        }
        return (
          <Chip
            icon={<PaymentIcon fontSize="small" />}
            label={pagos[0]?.descripcion || "N/A"}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              color: "#00AAE4",
              borderColor: alpha("#00AAE4", 0.5),
              "& .MuiChip-icon": { color: "#00AAE4" },
              display: "flex",
            }}
          />
        );
      },
    },
    {
      field: "descripcionEntidad",
      headerName: "Proveedor",
      width: 150,
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip
          title={`${params.row.descripcionEntidad} ${
            params.row.apellido || ""
          }`}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon
              fontSize="small"
              sx={{ color: alpha("#5b5b5f", 0.7) }}
            />
            <Typography variant="body2" noWrap>
              {params.row.descripcionEntidad} {params.row.apellido || ""}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "negocio",
      headerName: "Negocio",
      width: 150,
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StorefrontIcon
            fontSize="small"
            sx={{ color: alpha("#5b5b5f", 0.7) }}
          />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "ubicacion",
      headerName: "Ubicación",
      width: 150,
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocationOnIcon
            fontSize="small"
            sx={{ color: alpha("#5b5b5f", 0.7) }}
          />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "action",
      headerName: "Acciones",
      width: 150,
      flex: 1,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Ver detalles">
            <IconButton
              className="colorGeneralParaIconos"
              onClick={() => handleOpenModal(params.row)}
              size="small"
              sx={{
                bgcolor: alpha("#5b5b5f", 0.1),
                "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Descargar PDF">
            <IconButton
              className="colorGeneralParaIconos"
              onClick={() => handleDownloadPDF(params.row)}
              size="small"
              sx={{
                bgcolor: alpha("rgb(244, 177, 14)", 0.1),
                "&:hover": { bgcolor: alpha("rgb(244, 177, 14)", 0.2) },
              }}
            >
              <PictureAsPdfIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              className="colorGeneralParaIconos"
              onClick={() => handleEditarTransaccion(params.row)}
              size="small"
              sx={{
                bgcolor: alpha("rgb(244, 177, 14)", 0.1),
                "&:hover": { bgcolor: alpha("rgb(244, 177, 14)", 0.2) },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
           <Tooltip title="Pagar">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenMediosDePago(params.row)}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.2),
                            },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
          <Tooltip title="Borrar Transaccion">
            <IconButton
              className="colorGeneralParaIconos"
              onClick={() => borrarTransaccion(params.row)}
              size="small"
              sx={{
                bgcolor: alpha("rgb(244, 177, 14)", 0.1),
                "&:hover": { bgcolor: alpha("rgb(244, 177, 14)", 0.2) },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "estadoDeCompra",
      headerName: "Estado De Compra",
      width: 150,
      renderCell: (params) => {
        const description = params.value || "";
        const idEstado = params.row.idEstadoDeCompra;
        let textColor = "inherit";
        if (idEstado === 1) textColor = "orange";
        else if (idEstado === 2) textColor = "red";
        else if (idEstado === 3) textColor = "green";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PaymentIcon
              fontSize="small"
              sx={{ color: alpha("#5b5b5f", 0.7) }}
            />
            <Typography
              variant="body2"
              sx={{ color: textColor, fontWeight: "bold" }}
            >
              {description}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const borrarTransaccion = async (transaccionRow) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `Vas a eliminar la transacción con ID ${transaccionRow.id}. Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        // ejecuta la eliminación
        await updateDeleteTransaccion(transaccionRow.id);

        // refresca los datos (ajusta los args a tu fetchData)
        await fetchData(
          startDate,
          1,
          endDate,
          proveedor,
          selectedUbicacionFiltro,
          minCompras,
          maxCompras,
          100,
          estadoSeleccionado ? estadoSeleccionado : null,
          mostrarDeletes,
          idTransaccionSearch
        );

        Swal.fire({
          title: "Eliminado",
          text: `La transacción ${transaccionRow.id} ha sido eliminada.`,
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error al borrar transacción:", error);
        Swal.fire({
          title: "Error",
          text: "No se pudo eliminar la transacción. Intenta de nuevo.",
          icon: "error",
        });
      }
    }
  };

  const totalCompras = useMemo(() => {
    if (!sortedData || !Array.isArray(sortedData) || sortedData.length === 0)
      return 0;
    return sortedData.reduce((acc, row) => acc + (row.montoTotal || 0), 0);
  }, [sortedData]);

  const CustomFooter = () => (
    <GridFooterContainer>
      <div
        style={{
          padding: "10px",
          fontWeight: "bold",
          fontSize: "16px",
          textAlign: "right",
          width: "100%",
          backgroundColor: "#f5f5f5",
          borderTop: "1px solid #ddd",
        }}
      >
        Total Compras:{" "}
        <span style={{ color: "#4CAF50" }}>
          ${totalCompras.toLocaleString("es-AR")}
        </span>
      </div>
      <GridFooter />
    </GridFooterContainer>
  );

  const handleEditarTransaccion = (transaccionRow) => {
    navigate(
      `/editarCompraComponent/${transaccionRow.id}/${transaccionRow.idEstadoDeCompra}`
    );
  };

  const handleDownloadPDF = async (transaccionRow) => {
    try {
      const transaccionEncontrada = await getTransaccionItemID(
        transaccionRow.id
      );
      console.log("transaccionEncontrada: ", transaccionEncontrada)
      // Como solo queremos proveedores (entidades con ID 2), usamos getEntidadByID(2)
      const entidadProveedor = await getEntidadByID(2);
      const itemsTransaccion = await Promise.all(
        transaccionEncontrada.map(async (transItem) => {
          const foundItem = await getItemById(transItem.idItem)
          const precio = transItem.precio || 0
          return {
            idItem: transItem.idItem,
            codigo: foundItem ? foundItem.codigo : "N/A",
            descripcion: foundItem ? foundItem.descripcion : "Descripción no disponible",
            cantidad: transItem.cantidad || 0,
            precio: precio,
            porcentajeDescuento: transItem.porcentajeDescuento || 0,
            total: (precio * transItem.cantidad).toFixed(2),
          }
        }),
      )
      generarFacturaPDF(
        transaccionRow,
        itemsTransaccion,
        entidadProveedor,
        "tablaCompra",
        transaccionRow.impuestos
      );
    } catch (error) {
      console.error("Error al manejar la descarga del PDF:", error);
    }
  };

  // Obtener detalles de items relacionados
  useEffect(() => {
    const fetchItemsDetails = async () => {
      const itemsDetails = {};
      await Promise.all(
        transaccionItems.map(async (transaccionItem) => {
          const foundItem = await getItemById(transaccionItem.idItem);
          if (foundItem) {
            itemsDetails[transaccionItem.idItem] = foundItem;
          }
        })
      );
      setItemsMap(itemsDetails);
    };
    if (transaccionItems.length) {
      fetchItemsDetails();
    }
  }, [transaccionItems, getItemById]);

  const handleOpenModal = async (transaccionRow) => {
    try {
      const transaccionSelect = sortedData.find(
        (t) => t.id === transaccionRow.id
      );
      setSelectedTransaccion(transaccionSelect);
      const entidadProveedor = await getEntidadByID(2);
      setSelectedTransaccionEntidad(entidadProveedor);
      const items = await getTransaccionItemIDEnCompras(transaccionRow.id);
      setTransaccionItems(items.items);
      setTransaccionCompraItems(items.compraItems);
      setOpenModal(true);
    } catch (error) {
      console.error("Error al abrir el modal:", error);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Función para cerrar el modal de gráficas
  const handleCloseMedioDePago = () => {
    setAbrirMedioDePago(false);
  };

  const handleOpenMediosDePago = (datos) => {
    console.log("Datos recibidos para abrir medio de pago:", datos, datos.estadoDeCompra);
    if(datos.estadoDeCompra != "Cerrada" && datos.estadoDeCompra != "Stock Pendiente") {
      const updatedTotalMedioPagoList = datos.pagos.reduce((total, pago) => total + pago.monto, 0)
      console.log(updatedTotalMedioPagoList)
      setPrecioTotal(Math.abs(datos.montoTotal - updatedTotalMedioPagoList)); // Usar valor absoluto del saldo
      setSelectedTransaccion(datos); // Guardar los datos de la cuenta
      setAbrirMedioDePago(true);
    }
  };

  const handleConfirmarPago = async (
    mediosDePago,
    montoTotalMediosPago,
    transaccionSeleccionada,
    descripcion
  ) => {
    // Calcular si es pago completo o parcial
    console.log(mediosDePago)
        console.log(montoTotalMediosPago)

console.log(transaccionSeleccionada)

    const esPagoCompleto =
      Math.abs(montoTotalMediosPago) >= Math.abs(transaccionSeleccionada?.saldo);

    const descripcionFinal = descripcion || "Pago cuenta corriente";

      const procesarTransaccion = async (dato, cerrarCompra = false) => {
          try {
      
            console.log(dato);
              //Verificamos si restante es 0
              const restante = precioTotal - montoTotalMediosPago;
                console.log(dato);
                // Procesar medios de pago
                if (mediosDePago) {
                    for (const it of mediosDePago) {

                      const transaccionCrearPago = await postCrearPago(
                        1,
                        1,
                        it.id,
                        it.monto
                      );

                      const idTransaccionCrearPago = transaccionCrearPago.data.id;


                      if (it.tipoMedioDePago === 5) { //Verificamos si toda la lista de medios de pago existe id = 5 que seria cuenta corriente
                        const cuentas = await getCuentaCorrienteByIdEntidad(
                          transaccionSeleccionada.idEntidadNumero
                        ); //Todas las cuentas relacionadas a esta cuenta
                        const cuenta = cuentas.find( //Verificamos si existe esta cuenta o si hay que crearla
                          (ct) => ct.idEntidad === transaccionSeleccionada.idEntidadNumero
                        );
      
                        if (cuenta) {
                          await updateCuentaCorriente(
                            transaccionSeleccionada.idEntidadNumero,
                            it.monto,
                            "compra" //va compra para restar la cuenta corriente
                          );
                        } else {
                          await postCuentaCorriente(
                            transaccionSeleccionada.idEntidadNumero,
                            it.monto,
                            "compra" //va compra para restar la cuenta corriente
                          );
                        }
                      }
                      //Post transaccion del pago
      
                      await postTransaccionPago(
                        transaccionSeleccionada.id, //idTransaccion
                        idTransaccionCrearPago
                      );
                    }
                  
                }
      
            if (cerrarCompra) {
              console.log("Usuario eligió cerrar la compra. Estado: CERRADA");
              estadoCompraGlobal.current = 3; // Actualiza el estado global
              await updateTransaccionCompraEstado(transaccionSeleccionada.id, 3); // CERRADA
            } else {
              // 2. ¿Pagado completo?
              let estaPagado = false;
              const restante = precioTotal - montoTotalMediosPago;
      

                // Múltiples medios de pago → la suma debe cubrir el restante
                const sumaMedios = mediosDePago.reduce(
                  (total, item) => total + item.monto,
                  0
                );
                estaPagado = sumaMedios >= restante;
                            console.log('NUMEROS ',precioTotal, montoTotalMediosPago, restante, sumaMedios, estaPagado)


              // 1. ¿Stock completo?
              let stockSatisfecho = false;
              if (dato == "stock") {
                stockSatisfecho = transaccionItems.every(
                  (item) =>
                    (item.cantidadRecibida || 0) + (item.aRecibir || 0) >=
                    item.cantidad
                );
              }
              if (dato == "pagos") {
                stockSatisfecho = transaccionItems.every(
                  (item) => (item.cantidadRecibida || 0) >= item.cantidad
                );
              }
      
              if (!estaPagado && !stockSatisfecho) {
                console.log("No pagada y stock incompleto. Estado: ABIERTA");
                estadoCompraGlobal.current = 1;
                await updateTransaccionCompraEstado(transaccionSeleccionada.id, 1); // ABIERTA
              } else if (!estaPagado && stockSatisfecho) {
                console.log("No pagada. Estado: SALDO PENDIENTE");
                estadoCompraGlobal.current = 4;
                await updateTransaccionCompraEstado(transaccionSeleccionada.id, 4); // SALDO PENDIENTE
              } else if (estaPagado && !stockSatisfecho) {
                console.log("Pagada pero stock incompleto. Estado: SALDO CERRADO");
                estadoCompraGlobal.current = 5;
                await updateTransaccionCompraEstado(transaccionSeleccionada.id, 5); // SALDO CERRADO
              } else if (estaPagado && stockSatisfecho) {
                setAbrirMedioDePago(false);
                // Usamos SweetAlert2 para esperar la respuesta del usuario
                const MySwal = withReactContent(Swal);
                const result = await MySwal.fire({
                  title: "Confirmar cierre",
                  text: "La compra cumple los requisitos para ser cerrada. ¿Desea cerrarla ahora?",
                  icon: "question",
                  showCancelButton: true,
                  confirmButtonText: "Cerrar compra",
                  cancelButtonText: "No cerrar",
                  customClass: {
    container: 'swal2-override-zindex'
  }
                });
      
                // Si el usuario cancela, detenemos la función (o realizamos otra acción)
                if (!result.isConfirmed) {
                  estadoCompraGlobal.current = 1; // Actualiza el estado global
                  // Por ejemplo, llamamos a una función para dejarla en estado "sin cerrar"
                  await convertirSinCerrar(transaccionSeleccionada.id);
                  return; // Salimos de la función
                } else {
                  estadoCompraGlobal.current = 3; // Actualiza el estado global
                  // El usuario confirmó: procedemos a cerrar la compra
                  await cerrarLaCompra(transaccionSeleccionada.id);
                }
              }
            }
      
            withReactContent(Swal).fire({
              position: "bottom-end",
              icon: "success",
              text: "Compra actualizada correctamente",
              showConfirmButton: false,
              timer: 3000,
              width: 300,
            });
          } catch (error) {
            console.error("Error al actualizar la transacción:", error);
            withReactContent(Swal).fire({
              position: "bottom-end",
              icon: "error",
              text: "Error al actualizar la compra",
              showConfirmButton: false,
              timer: 3000,
              width: 300,
            });
          }
        };

        await procesarTransaccion("pagos");
        setAbrirMedioDePago(false);
        await fetchData(
      startDate,
      1,
      endDate,
      proveedor,
      selectedUbicacionFiltro,
      minCompras,
      maxCompras,
      100,
      estadoSeleccionado ? estadoSeleccionado : null,
      mostrarDeletes,
      idTransaccionSearch
    );
    
  };

    const convertirSinCerrar = async (idTransaccionEditarCompra) => {
    estadoCompraGlobal.current = 1; // Actualiza el estado global
    await updateTransaccionCompraEstado(idTransaccionEditarCompra, 1); // ABIERTA
  };
  const cerrarLaCompra = async (idTransaccionEditarCompra) => {
    estadoCompraGlobal.current = 3; // Actualiza el estado global
    await updateTransaccionCompraEstado(idTransaccionEditarCompra, 3); //CERRADA
  };



  /*if (loading) {
    return <SpinnerLading />
  }*/

  return (
    <div
      className="tablaVentasContainer"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        position: "relative",
        padding: "16px",
      }}
    >
      <ThemeProvider theme={theme}>
        {/* Panel de filtros */}
        <Paper
          className="diseñoFiltroTransaccionesVenta"
          elevation={3}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Filtros de fechas */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                flex: 1,
                minWidth: 250,
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <IconButton onClick={decreaseDate} color="primary" size="small">
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <DatePicker
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="DD/MM/YYYY"
                  label="Desde"
                  slotProps={{
                    textField: { size: "small", inputProps: { maxLength: 10 } },
                  }}
                  sx={{ width: 150 }}
                />
                <DatePicker
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="DD/MM/YYYY"
                  label="Hasta"
                  slotProps={{
                    textField: { size: "small", inputProps: { maxLength: 10 } },
                  }}
                  sx={{ width: 150 }}
                />
                <IconButton onClick={increaseDate} color="primary" size="small">
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </LocalizationProvider>
            </Box>

            {/* Filtros de montos */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: 200,
              }}
            >
              <TextField
                label="Mínimo"
                type="number"
                size="small"
                value={minCompras}
                onChange={handleMinChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                sx={{ width: 100 }}
              />
              <TextField
                label="Máximo"
                type="number"
                size="small"
                value={maxCompras}
                onChange={handleMaxChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                sx={{ width: 100 }}
              />
            </Box>

            {/* Filtros de proveedor, ubicación y estado */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 2,
                minWidth: 300,
              }}
            >
              <TextField
                label="Buscar por ID"
                type="number"
                size="small"
                value={idTransaccionSearch}
                onChange={(e) => setIdTransaccionSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: "20%" }}
              />
              <Autocomplete
                size="small"
                disablePortal
                options={
                  Array.isArray(entidades)
                    ? entidades.map((it) => ({
                        label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
                        ...it,
                      }))
                    : []
                }
                value={proveedor}
                onChange={(event, newValue) => setProveedor(newValue || null)}
                onInputChange={(event, newInputValue) =>
                  setSearchTextProveedor(newInputValue)
                }
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField {...params} label="Proveedor" variant="outlined" />
                )}
              />
              <Autocomplete
                size="small"
                disablePortal
                options={
                  Array.isArray(ubicaciones)
                    ? ubicaciones.map((it) => ({
                        id: it.id,
                        label: it.descripcion,
                      }))
                    : []
                }
                value={selectedUbicacionFiltro}
                onChange={(event, newValue) =>
                  setSelectedUbicacionFiltro(newValue)
                }
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField {...params} label="Ubicación" variant="outlined" />
                )}
              />
              <Autocomplete
                size="small"
                disablePortal
                options={
                  Array.isArray(estados)
                    ? estados.map((it) => ({
                        id: it.id,
                        label: it.descripcion,
                      }))
                    : []
                }
                value={estadoSeleccionado}
                onChange={(event, newValue) => setEstadoSeleccionado(newValue)}
                getOptionLabel={(option) => option.label || ""}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField {...params} label="Estados" variant="outlined" />
                )}
              />
              {/* CheckBox para mostrar eliminados */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">Mostrar eliminados</Typography>
                <Checkbox
                  checked={mostrarDeletes}
                  onChange={(e) => setMostrarDeletes(e.target.checked)}
                  color="primary"
                />
              </Box>
            </Box>

            {/* Botones de acción */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={buttonFiltrado}
                startIcon={<SearchIcon />}
                sx={{
                  backgroundColor: "#5b5b5f",
                  textTransform: "none",
                  padding: "8px 16px",
                  color: "white",
                  "&:hover": {
                    backgroundColor: alpha("rgb(244, 177, 14)", 0.8),
                  },
                }}
              >
                Buscar
              </Button>
              <Tooltip
                title={
                  proveedor ||
                  selectedUbicacionFiltro ||
                  minCompras > 0 ||
                  maxCompras > 0 ||
                  estadoSeleccionado
                    ? "Resetear filtros"
                    : "Refrescar"
                }
              >
                <IconButton
                  onClick={resetFilters}
                  color="primary"
                  size="small"
                  sx={{
                    bgcolor:
                      proveedor ||
                      selectedUbicacionFiltro ||
                      minCompras > 0 ||
                      maxCompras > 0 ||
                      estadoSeleccionado
                        ? alpha(theme.palette.secondary.main, 0.1)
                        : "transparent",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.secondary.main, 0.2),
                    },
                  }}
                >
                  {proveedor ||
                  selectedUbicacionFiltro ||
                  minCompras > 0 ||
                  maxCompras > 0 ||
                  estadoSeleccionado ? (
                    <FilterAltOffIcon />
                  ) : (
                    <ReplayIcon />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>

        {/* Encabezado de la tabla */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold" color="#5b5b5f">
            Compras
          </Typography>
       
        </Box>

        {/* Tabla principal */}
        <Paper
          elevation={3}
          sx={{
            flexGrow: 1,
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
            height: "calc(100vh - 120px)",
            minHeight: "500px",
          }}
        >
          <div style={{ height: "100%", width: "100%" }}>
            <DataGrid
key={isNavbarCollapsed ? "collapsed" : "expanded"}
              className="tablaVentasDataGrid"
              rows={sortedData}
              columns={columns}
              getRowId={(row) => row.id}
              getRowHeight={() => 35}
              headerHeight={35}  
              pageSize={5}
              sx={{
                width: "100%",
                border: 0,
                "& .MuiDataGrid-footerContainer": {
                  display: "none !important",
                },
                "& .MuiDataGrid-columnHeaders": { //MANEJA CSS HEADER
                  backgroundColor: "#5b5b5f",
                  color: "white",
                  borderBottom: "2px solid #333",
                  height: "45px !important",
                  minHeight: "45px !important",
                  maxHeight: "45px !important",
                },
                "& .MuiDataGrid-columnHeader": { //MANEJA CSS HEADER
                  height: "45px !important",
                  minHeight: "45px !important",
                  maxHeight: "45px !important",
                },
                "& .MuiDataGrid-columnHeaderTitle": { //MANEJA CSS HEADER
                  color: "white !important",
                  lineHeight: "45px !important",
                },
                "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                  color: "white !important",
                },
                "& .MuiDataGrid-menuIconButton": {
                  color: "rgb(244, 177, 14) !important",
                },
                "& .MuiInputBase-root": {
                  color: "rgb(244, 177, 14) !important",
                },
                "& .MuiTablePagination-root": {
                  color: "black !important",
                },
                "& .MuiSelect-icon": {
                  color: "rgb(244, 177, 14) !important",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                "& .MuiDataGrid-row": {
                  
                  "&:nth-of-type(even)": {
                    backgroundColor: "#F7F7F7",
                  },
                  "&:hover": {
                    backgroundColor: "#E3EAFD",
                    transition: "background 0.3s ease",
                  },
                },
                "& .row-devolucion": {
                  backgroundColor: alpha("#9e9e9e !important", 0.05),
                  "&:hover": {
                    backgroundColor: alpha("#9e9e9e !important", 0.1),
                  },
                },
                "& .MuiDataGrid-virtualScroller": {
                  lineHeight: "none !important",
                  height: "10px !imporant",
                },
                "& .MuiDataGrid-footerContainer": {
                  position: "sticky",
                  bottom: 0,
                  backgroundColor: "#ffffff",
                  borderTop: "1px solid #ddd",
                  zIndex: 10,
                },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                  width: "2px",
                },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                  borderRadius: "10px",
                },
              }}
              components={{ Footer: CustomFooter }}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0
                  ? "row-color-alt"
                  : "row-color-default"
              }
            />
          </div>
        </Paper>

        {/* Paginación */}
        <Box sx={{ flexShrink: 0, p: 1 }}>
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={montoTotalTransacciones}
            vieneDe={"tablaCompras"}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </Box>

        <ModalMedioDePago
          open={abrirMedioDePago}
          handleClose={handleCloseMedioDePago}
          precioTotal={precioTotal}
          transaccionSeleccionada={selectedTransaccion}
          onConfirmarPago={handleConfirmarPago}
        />

        {/* Modal de visualización */}
        <ModalViewTablaCompra
          openModal={openModal}
          handleCloseModal={handleCloseModal}
          selectedTransaccion={selectedTransaccion}
          selectedTransaccionEntidad={selectedTransaccionEntidad}
          transaccionItems={transaccionItems}
          compraItems={compraItems}
          montoTotalTransacciones={totalCompras}
        />
      </ThemeProvider>
    </div>
  );
}
